import { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { getUserStats, getBadges } from '../services/userApi';
import { getMyLeagueStatus } from '../services/leagueApi';
import { formatTime, formatTimeHuman } from '../utils/formatters';
import BadgeDisplay from '../components/profile/BadgeDisplay';
import LeagueBadge from '../components/league/LeagueBadge';

function MyStats() {
  const { user, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [badges, setBadges] = useState({ definitions: {}, earned: [] });
  const [leagueStatus, setLeagueStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Redirect if not logged in
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  // Load stats and badges on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [statsData, badgesData, leagueData] = await Promise.all([
          getUserStats(),
          getBadges(),
          getMyLeagueStatus().catch(() => null),
        ]);
        setStats(statsData);
        setBadges(badgesData);
        if (leagueData) setLeagueStatus(leagueData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadData();
    }
  }, [user]);

  // Format last completed date
  const formatLastCompleted = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-puzzle-blue" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-puzzle-blue" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-puzzle-background py-8">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-puzzle-surface rounded-lg shadow p-6 text-center">
            <p className="text-puzzle-danger">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="mt-4 text-puzzle-blue hover:underline"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const {
    completions,
    totalCompletions,
    totalPlayTime,
    totalMoves,
    avgPlayTime,
    avgMoves,
  } = stats?.stats || {};

  return (
    <div className="min-h-screen bg-puzzle-background py-8">
      <div className="max-w-2xl mx-auto px-4">
        <h1 className="text-2xl font-bold text-puzzle-text mb-8">My Puzzle Stats</h1>

        {/* Summary Cards */}
        <section className="bg-puzzle-surface rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-puzzle-text mb-4">Lifetime Stats</h2>

          <div className="grid grid-cols-3 gap-4">
            {/* Total Completions */}
            <div className="bg-puzzle-background rounded-xl p-4 text-center">
              <div className="text-3xl mb-1">🧩</div>
              <div className="text-2xl font-bold text-puzzle-text">
                {totalCompletions || 0}
              </div>
              <div className="text-sm text-puzzle-text-secondary">
                Puzzles Completed
              </div>
            </div>

            {/* Total Time */}
            <div className="bg-puzzle-background rounded-xl p-4 text-center">
              <div className="text-3xl mb-1">⏱️</div>
              <div className="text-2xl font-bold text-puzzle-text">
                {formatTimeHuman(totalPlayTime || 0)}
              </div>
              <div className="text-sm text-puzzle-text-secondary">
                Total Play Time
              </div>
            </div>

            {/* Total Moves */}
            <div className="bg-puzzle-background rounded-xl p-4 text-center">
              <div className="text-3xl mb-1">🎯</div>
              <div className="text-2xl font-bold text-puzzle-text">
                {(totalMoves || 0).toLocaleString()}
              </div>
              <div className="text-sm text-puzzle-text-secondary">
                Total Moves
              </div>
            </div>
          </div>
        </section>

        {/* By Puzzle Type */}
        <section className="bg-puzzle-surface rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-puzzle-text mb-4">By Puzzle Type</h2>

          <div className="space-y-4">
            {/* Daily Puzzles */}
            <div className="bg-puzzle-background rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">📅</span>
                  <div>
                    <div className="font-medium text-puzzle-text">Daily Puzzles</div>
                    <div className="text-sm text-puzzle-text-secondary">
                      Last completed:
                      {' '}
                      {formatLastCompleted(completions?.daily?.lastCompletedAt)}
                    </div>
                  </div>
                </div>
                <div className="text-2xl font-bold text-puzzle-blue">
                  {completions?.daily?.count || 0}
                </div>
              </div>
            </div>

            {/* Solo Puzzles */}
            <div className="bg-puzzle-background rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🔒</span>
                  <div>
                    <div className="font-medium text-puzzle-text">Solo Puzzles</div>
                    <div className="text-sm text-puzzle-text-secondary">
                      Last completed:
                      {' '}
                      {formatLastCompleted(completions?.solo?.lastCompletedAt)}
                    </div>
                  </div>
                </div>
                <div className="text-2xl font-bold text-puzzle-green">
                  {completions?.solo?.count || 0}
                </div>
              </div>
            </div>

            {/* Room Puzzles */}
            <div className="bg-puzzle-background rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">👥</span>
                  <div>
                    <div className="font-medium text-puzzle-text">Room Puzzles</div>
                    <div className="text-sm text-puzzle-text-secondary">
                      Last completed:
                      {' '}
                      {formatLastCompleted(completions?.room?.lastCompletedAt)}
                    </div>
                  </div>
                </div>
                <div className="text-2xl font-bold text-puzzle-yellow">
                  {completions?.room?.count || 0}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Averages */}
        <section className="bg-puzzle-surface rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-puzzle-text mb-4">Averages</h2>

          <div className="grid grid-cols-2 gap-4">
            {/* Average Time */}
            <div className="bg-puzzle-background rounded-xl p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <svg className="w-5 h-5 text-puzzle-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-puzzle-text-secondary">Avg Time</span>
              </div>
              <div className="text-xl font-bold text-puzzle-text">
                {avgPlayTime ? formatTime(Math.round(avgPlayTime)) : '--:--'}
              </div>
              <div className="text-xs text-puzzle-text-secondary mt-1">per puzzle</div>
            </div>

            {/* Average Moves */}
            <div className="bg-puzzle-background rounded-xl p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <svg className="w-5 h-5 text-puzzle-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                </svg>
                <span className="text-puzzle-text-secondary">Avg Moves</span>
              </div>
              <div className="text-xl font-bold text-puzzle-text">
                {avgMoves ? Math.round(avgMoves) : '--'}
              </div>
              <div className="text-xs text-puzzle-text-secondary mt-1">per puzzle</div>
            </div>
          </div>
        </section>

        {/* Streak Info */}
        {stats?.streak && (
          <section className="bg-puzzle-surface rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold text-puzzle-text mb-4">Daily Streak</h2>

            <div className="grid grid-cols-2 gap-4">
              {/* Current Streak */}
              <div className="bg-puzzle-background rounded-xl p-4 text-center">
                <div className="text-3xl mb-1">🔥</div>
                <div className="text-2xl font-bold text-orange-500">
                  {stats.streak.current || 0}
                </div>
                <div className="text-sm text-puzzle-text-secondary">
                  Current Streak
                </div>
              </div>

              {/* Longest Streak */}
              <div className="bg-puzzle-background rounded-xl p-4 text-center">
                <div className="text-3xl mb-1">🏆</div>
                <div className="text-2xl font-bold text-yellow-500">
                  {stats.streak.longest || 0}
                </div>
                <div className="text-sm text-puzzle-text-secondary">
                  Best Streak
                </div>
              </div>
            </div>
          </section>
        )}

        {/* League Status */}
        <section className="bg-puzzle-surface rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-puzzle-text mb-4">League</h2>
          {leagueStatus?.joined ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <LeagueBadge tier={leagueStatus.tier} size="md" />
                <div>
                  <div className="text-sm text-puzzle-text-secondary">
                    All-time:
                    {' '}
                    <span className="font-semibold text-puzzle-text">
                      {leagueStatus.currentRating}
                      {' '}
                      pts
                    </span>
                  </div>
                  <div className="text-sm text-puzzle-text-secondary">
                    This week:
                    {' '}
                    <span className="font-semibold text-puzzle-text">
                      {leagueStatus.weeklyRating}
                      {' '}
                      pts
                    </span>
                    {' '}
                    &middot; Rank #
                    {leagueStatus.rankInTier}
                  </div>
                </div>
              </div>
              <Link
                to="/league"
                className="text-puzzle-blue hover:underline text-sm font-medium"
              >
                View League
              </Link>
            </div>
          ) : (
            <div className="text-center py-2">
              <p className="text-sm text-puzzle-text-secondary mb-2">
                Join the leagues to compete in ranked divisions!
              </p>
              <Link
                to="/league"
                className="text-puzzle-blue hover:underline text-sm font-medium"
              >
                Join Leagues
              </Link>
            </div>
          )}
        </section>

        {/* Badges */}
        <section className="bg-puzzle-surface rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-puzzle-text mb-4">Badges</h2>
          <p className="text-sm text-puzzle-text-secondary mb-4">
            Earn badges by completing daily puzzles and building streaks.
          </p>
          <BadgeDisplay
            definitions={badges.definitions}
            earned={badges.earned}
          />
        </section>
      </div>
    </div>
  );
}

export default MyStats;
